import React from "react";
import {
  ShieldCheck,
  Network,
  Activity,
  MapPin,
  Award,
  CheckCircle2,
  Loader2,
  Clock,
  Sparkles,
} from "lucide-react";

export const THREE_WAY_ASPECTS = [
  {
    id: 1,
    name: "Donor Registry & Active Availability Screening",
    shortName: "Active Donor Check",
    description:
      "Filtering registered donors by active availability status and selected regional boundaries.",
    detail:
      "Eliminates inactive donors to ensure immediate clinical exchange feasibility.",
    icon: ShieldCheck,
  },
  {
    id: 2,
    name: "3-Way Permutation Graph Traversal",
    shortName: "Permutation Graph",
    description:
      "Scanning closed-loop permutations: Recipient 1 ← Donor 2, Recipient 2 ← Donor 3, Recipient 3 ← Donor 1.",
    detail:
      "Explores all directed 3-way cycles across 480+ participant pairs without broken chains.",
    icon: Network,
  },
  {
    id: 3,
    name: "ABO Isoagglutinin Blood Compatibility Matrix",
    shortName: "ABO Matrix Verification",
    description:
      "Verifying reciprocal non-reactive antigen compatibility across all 3 exchange legs simultaneously.",
    detail:
      "Enforces strict multi-leg blood group compatibility rules to prevent hyperacute rejection.",
    icon: Activity,
  },
  {
    id: 4,
    name: "Logistical Proximity & Clinical Documentation",
    shortName: "Logistics & Readiness",
    description:
      "Scoring state/city logistical proximity, transport windows, and clinical record completeness.",
    detail:
      "Favors candidates within close geographic reach and with full medical documentation.",
    icon: MapPin,
  },
  {
    id: 5,
    name: "Viability Optimization & Best Match Ranking",
    shortName: "Best Match Selection",
    description:
      "Computing multi-factor viability scores (100–200 pts) and isolating the highest-tier clinical match.",
    detail:
      "Ranks candidate cycles so the safest, most actionable option is surfaced as Rank #1.",
    icon: Award,
  },
];

export const TWO_WAY_ASPECTS = [
  {
    id: 1,
    name: "Donor Registry & Active Availability Screening",
    shortName: "Active Donor Check",
    description:
      "Filtering candidate donors for active availability and regional criteria.",
    detail:
      "Ensures both paired donors are clinically active and available for surgery.",
    icon: ShieldCheck,
  },
  {
    id: 2,
    name: "ABO Blood Group Compatibility Matrix",
    shortName: "ABO Compatibility",
    description:
      "Verifying reciprocal non-reactive antigen compatibility between paired donors and recipients.",
    detail:
      "Validates two-way mutual donor-recipient blood compatibility.",
    icon: Activity,
  },
  {
    id: 3,
    name: "HLA / Virtual Crossmatch & CREG Analysis",
    shortName: "Virtual Crossmatch",
    description:
      "Screening donor-specific anti-HLA antibodies and histocompatibility crossmatch profiles.",
    detail:
      "Analyzes single-antigen bead and cross-reactive epitope groups for immunological safety.",
    icon: Network,
  },
  {
    id: 4,
    name: "Logistical Proximity & Clinical Documentation",
    shortName: "Logistics & Records",
    description:
      "Calculating geographic proximity (state/city) and medical record readiness.",
    detail:
      "Evaluates travel feasibility and documentation completeness across paired candidates.",
    icon: MapPin,
  },
  {
    id: 5,
    name: "Viability Optimization & Best Match Ranking",
    shortName: "Best Match Selection",
    description:
      "Ranking compatible pairs by comprehensive clinical score to surface the optimal match.",
    detail:
      "Isolates the highest-probability, safest paired kidney exchange candidate.",
    icon: Award,
  },
];

const MatchProgressIndicator = ({
  progress = 0,
  currentStage = 0,
  isThreeWay = true,
  recipientName = "",
  donorName = "",
}) => {
  const aspects = isThreeWay ? THREE_WAY_ASPECTS : TWO_WAY_ASPECTS;
  const currentAspect = aspects[Math.min(currentStage, aspects.length - 1)];
  const isComplete = progress >= 100;

  return (
    <div className="w-full max-w-4xl mx-auto my-8 bg-white border border-purple-100 rounded-2xl shadow-xl overflow-hidden animate-fadeIn">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-purple-700 via-indigo-700 to-purple-800 p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white backdrop-blur-sm">
                <Sparkles className="w-3.5 h-3.5 mr-1 text-yellow-300" />
                {isThreeWay ? "3-Way Closed-Loop Matching Engine" : "2-Way Paired Matching Engine"}
              </span>
              <span className="text-xs text-purple-200">
                Phase {Math.min(currentStage + 1, aspects.length)} of {aspects.length}
              </span>
            </div>
            <h3 className="text-2xl font-bold text-white tracking-tight">
              {isComplete
                ? "Optimal Matches Identified!"
                : "Analyzing Clinical Viability & Best Match..."}
            </h3>
            {(recipientName || donorName) && (
              <p className="text-xs text-purple-200 mt-1">
                Searching optimal exchange for: <span className="font-semibold text-white">{recipientName || "Recipient"}</span> + <span className="font-semibold text-white">{donorName || "Donor"}</span>
              </p>
            )}
          </div>

          {/* Large Percentage Badge */}
          <div className="flex items-center sm:flex-col items-end justify-center bg-white/10 backdrop-blur-md px-4 py-2.5 rounded-xl border border-white/10">
            <span className="text-3xl font-extrabold text-white tracking-tight">
              {Math.min(Math.round(progress), 100)}%
            </span>
            <span className="text-[11px] uppercase tracking-wider text-purple-200 font-medium ml-2 sm:ml-0">
              {isComplete ? "Completed" : "Computing"}
            </span>
          </div>
        </div>

        {/* Progress Bar Container */}
        <div className="mt-6">
          <div className="flex justify-between items-center text-xs text-purple-200 mb-2 font-medium">
            <span>
              Aspect {Math.min(currentStage + 1, aspects.length)}:{" "}
              <strong className="text-white">{currentAspect?.shortName}</strong>
            </span>
            <span>{isComplete ? "100% Complete" : "Evaluating multi-factor criteria..."}</span>
          </div>
          <div className="w-full h-3 bg-purple-950/40 rounded-full overflow-hidden p-0.5 border border-white/20">
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 transition-all duration-300 ease-out relative"
              style={{ width: `${Math.min(Math.max(progress, 3), 100)}%` }}
            >
              <div className="absolute inset-0 bg-white/20 animate-pulse" />
            </div>
          </div>
        </div>
      </div>

      {/* Active Aspect Spotlight Card */}
      <div className="p-6 bg-gradient-to-br from-purple-50/70 via-white to-indigo-50/50 border-b border-purple-100">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-purple-200">
            {isComplete ? (
              <CheckCircle2 className="w-7 h-7 text-emerald-300" />
            ) : (
              <Loader2 className="w-7 h-7 animate-spin text-purple-100" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                {isComplete ? "Match Complete" : "Current Aspect in Progress"}
              </span>
              {!isComplete && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-600"></span>
                </span>
              )}
            </div>
            <h4 className="text-lg font-bold text-gray-900 mt-1">
              {currentAspect?.name}
            </h4>
            <p className="text-sm text-gray-600 mt-1">
              {currentAspect?.description}
            </p>
            <p className="text-xs text-purple-800 font-medium mt-2 flex items-center gap-1.5 bg-white/80 p-2 rounded-lg border border-purple-100/80">
              <Sparkles className="w-3.5 h-3.5 text-purple-600 shrink-0" />
              <span>{currentAspect?.detail}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Step-by-Step Aspect Tracker */}
      <div className="p-6 bg-white">
        <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
          Clinical Verification Pipeline ({aspects.length} Aspects)
        </h5>

        <div className="space-y-3">
          {aspects.map((aspect, idx) => {
            const isFinished = currentStage > idx || isComplete;
            const isCurrent = currentStage === idx && !isComplete;
            const Icon = aspect.icon;

            return (
              <div
                key={aspect.id}
                className={`flex items-center justify-between p-3.5 rounded-xl border transition-all duration-300 ${
                  isCurrent
                    ? "bg-purple-50/80 border-purple-300 shadow-sm"
                    : isFinished
                    ? "bg-emerald-50/40 border-emerald-200"
                    : "bg-gray-50/60 border-gray-200 opacity-60"
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      isFinished
                        ? "bg-emerald-500 text-white"
                        : isCurrent
                        ? "bg-purple-600 text-white animate-pulse"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-500">
                        0{idx + 1}
                      </span>
                      <span
                        className={`text-sm font-semibold truncate ${
                          isFinished
                            ? "text-gray-900"
                            : isCurrent
                            ? "text-purple-900 font-bold"
                            : "text-gray-600"
                        }`}
                      >
                        {aspect.name}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 truncate hidden sm:block">
                      {aspect.shortName} • {aspect.detail}
                    </p>
                  </div>
                </div>

                <div className="shrink-0 ml-3">
                  {isFinished ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      Verified
                    </span>
                  ) : isCurrent ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                      <Loader2 className="w-3.5 h-3.5 text-purple-600 animate-spin" />
                      Working...
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-gray-400">
                      <Clock className="w-3 h-3 text-gray-400" />
                      Pending
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Note */}
        <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
          <span>Priority logic: Active status + Geographic proximity + Complete HLA profile</span>
          <span className="font-semibold text-purple-600">Delivering Top Viable Candidate</span>
        </div>
      </div>
    </div>
  );
};

export default MatchProgressIndicator;
